import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormValues, validation } from './GoalDescription.meta';
import { StepCard } from '../../../PulseCheck.styles';
import { cardAnimationVariants } from '../../../../BeFi20/BeFi20.meta';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Textarea } from '../../../../CommonComponents/OPL/Textarea/Textarea';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { VisualPickerButton } from '../../../../CommonComponents/OPL/VisualPickerButton/VisualPickerButton';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { useSetGoalContext } from '../../context/useSetGoalContext';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { FORM_ID } from '../../SetGoal.meta';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';

export const GoalDescription = () => {
  const { wizard, setWizard, onSuccess, options } = useSetGoalContext();

  const [showIdeas, setShowIdeas] = useState(false);

  const { control, register, formState, handleSubmit, setValue, setFocus } = useForm<FormValues>({
    defaultValues: {
      goalDescription: wizard.goalDescription || '',
      isFinancialGoal: wizard.isFinancialGoal,
    },
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWizard = { ...wizard, ...values };

    setWizard(newWizard);

    onSuccess(newWizard);
  });

  return (
    <StepWrapper as="form" id={FORM_ID} onSubmit={onSubmit}>
      <StepCard variants={cardAnimationVariants} initial="enter" animate="visible">
        <Text as="h1" fontSize="h4" fontWeight={600} mb="16">
          {options.Title}
        </Text>
        <Text as="p" mb="24">
          {options.Subtitle}
        </Text>

        <Textarea
          {...register('goalDescription')}
          maxLength={250}
          rows={4}
          aria-label="Goal description"
          placeholder="Write my own..."
          formVariant={formState.errors?.goalDescription && 'error'}
          helperText={formState.errors?.goalDescription?.message}
        />

        <Text fontSize="small" fontWeight={600} color="neutral.600" mt="16" mb="8">
          Will you need to do any spending or saving to achieve this goal?
        </Text>

        <Controller
          control={control}
          name="isFinancialGoal"
          render={({ field: { value, name, onChange } }) => {
            return (
              <Box display="flex" flexDirection="column">
                <Radio name={name} checked={value === true} onChange={() => onChange(true)} mb="8">
                  Yes
                </Radio>
                <Radio name={name} checked={value === false} onChange={() => onChange(false)}>
                  No
                </Radio>

                {formState.errors?.isFinancialGoal && (
                  <HelperText variant="error" mt="4">
                    {formState.errors.isFinancialGoal.message}
                  </HelperText>
                )}
              </Box>
            );
          }}
        />

        <VisualPickerButton
          onClick={() => setShowIdeas(!showIdeas)}
          rightText={
            <Icon
              className={showIdeas ? 'far fa-minus' : 'far fa-plus'}
              color="neutral.500"
              fontSize="h4"
            />
          }
          my="16"
        >
          Need ideas to set a goal?
        </VisualPickerButton>

        {showIdeas && (
          <Box display="flex" flexDirection="column" css="gap: 16px">
            {options.Ideas.map((idea, index) => (
              <AnimatedBox
                key={idea}
                initial={{ opacity: 0, marginTop: -10 }}
                animate={{ opacity: 1, marginTop: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <VisualPickerButton
                  onClick={() => {
                    setValue('goalDescription', idea, { shouldValidate: true, shouldDirty: true });
                    setFocus('goalDescription');
                    setShowIdeas(false);
                  }}
                  css="width: 100%"
                >
                  {idea}
                </VisualPickerButton>
              </AnimatedBox>
            ))}
          </Box>
        )}
      </StepCard>
    </StepWrapper>
  );
};

const AnimatedBox = motion(Box);
